// Browser speech recognition (Chrome / Web Speech API) returns unpunctuated,
// lowercase text. These helpers turn spoken punctuation commands into symbols
// and apply basic sentence casing.

// Spoken command -> symbol. Words that also show up in real answers
// ("period", "colon", "dash", "point") are left out so they aren't mangled.
const SPOKEN_PUNCTUATION = [
    [/\bfull ?stop\b/gi, "."],
    [/\bcomma\b/gi, ","],
    [/\bquestion mark\b/gi, "?"],
    [/\bexclamation (?:mark|point)\b/gi, "!"],
    [/\bsemicolon\b/gi, ";"],
    [/\bopen (?:bracket|parenthesis)\b/gi, "("],
    [/\bclose (?:bracket|parenthesis)\b/gi, ")"],
];

const MARK_STRENGTH = { ",": 1, ";": 2, ".": 3, "!": 4, "?": 4 };

// ---- Pause-based punctuation ----------------------------------------------
// Chrome emits no results while the speaker is silent, so the gap between
// transcript updates approximates the pause length. Gaps include ~0.2-0.5s of
// recognition latency, hence the slightly high thresholds.
export const COMMA_PAUSE_MS = 600;
export const SENTENCE_PAUSE_MS = 1200;

// A phrase can't end on these, so a pause after them is hesitation, not a break
const NO_BREAK_AFTER = new Set([
    "a", "an", "the", "of", "to", "and", "or", "but", "is", "are", "was", "were", "be",
    "because", "with", "in", "on", "for", "that", "which", "by", "as", "at", "from", "if",
    "than", "so", "its", "their", "my", "your", "our", "his", "her", "this", "these",
    "those", "very", "more", "most", "less", "not", "into", "about", "can", "will",
    "would", "should", "could", "has", "have", "had", "do", "does", "did", "when", "where",
]);

// A long pause before these still continues the sentence -> comma, not full stop
const CONTINUES_SENTENCE = new Set([
    "and", "or", "but", "because", "so", "which", "that", "who", "whose", "where", "when",
    "while", "although", "though", "since", "unless", "until", "than", "as", "if", "whereas",
]);

// Sentences opening with these are treated as questions
const QUESTION_STARTERS = new Set([
    "what", "why", "how", "who", "is", "are", "do", "does", "did", "can", "could",
    "would", "should", "will",
]);

// Two-word spoken commands; pauses inside them must not be punctuated
const SPOKEN_TERMINATORS = new Set([
    "full stop", "question mark", "exclamation mark", "exclamation point",
]);
const SPOKEN_COMMAND_PAIRS = new Set([
    ...SPOKEN_TERMINATORS, "open bracket", "close bracket", "open parenthesis", "close parenthesis",
]);

const bareWord = (word = "") => word.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "");

/**
 * Inserts commas / full stops / question marks at detected pauses.
 * @param {string} transcript - raw transcript
 * @param {{ afterWord: number, gapMs: number }[]} pauses - pause after the Nth word (1-based count)
 * @param {{ final?: boolean }} options - final also closes the last sentence
 */
export const applyPausePunctuation = (transcript, pauses = [], { final = false } = {}) => {
    const words = (transcript || "").trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "";

    // Longest pause recorded after each word index
    const pauseAfter = new Map();
    for (const { afterWord, gapMs } of pauses) {
        if (afterWord <= 0 || afterWord >= words.length) continue;
        pauseAfter.set(afterWord - 1, Math.max(gapMs, pauseAfter.get(afterWord - 1) || 0));
    }

    const lastIndex = words.length - 1;
    let sentenceStart = 0;

    return words
        .map((word, i) => {
            const bare = bareWord(word);
            const nextBare = bareWord(words[i + 1]);
            const isQuestion = QUESTION_STARTERS.has(bareWord(words[sentenceStart]));
            let mark = null;

            if (/[.,?!;:)]$/.test(word)) {
                // Already punctuated by the recognizer
            } else if (final && i === lastIndex) {
                mark = ".";
            } else if (
                pauseAfter.has(i) &&
                !NO_BREAK_AFTER.has(bare) &&
                !SPOKEN_COMMAND_PAIRS.has(`${bare} ${nextBare}`) // don't split "full | stop"
            ) {
                mark = pauseAfter.get(i) >= SENTENCE_PAUSE_MS ? "." : ",";
                // "...skewed. Because" -> "...skewed, because" — but a question still ends
                if (mark === "." && !isQuestion && CONTINUES_SENTENCE.has(nextBare)) mark = ",";
            }

            if (mark === "." && isQuestion) mark = "?";

            // Next sentence starts after a terminal mark, typed or spoken ("full stop")
            const prevBare = bareWord(words[i - 1]);
            if (/[.?!]$/.test(word) || mark === "." || mark === "?" ||
                SPOKEN_TERMINATORS.has(`${prevBare} ${bare}`)) {
                sentenceStart = i + 1;
            }

            return mark ? word + mark : word;
        })
        .join(" ");
};

/**
 * @param {string} spoken - raw transcript
 * @param {{ capitalizeStart?: boolean }} options - false when continuing a sentence
 */
export const formatDictation = (spoken, { capitalizeStart = true } = {}) => {
    if (!spoken) return "";

    let text = spoken;
    for (const [pattern, symbol] of SPOKEN_PUNCTUATION) {
        text = text.replace(pattern, symbol);
    }

    text = text
        .replace(/\s+([.,?!;)])/g, "$1") // "word ," -> "word,"
        .replace(/\(\s+/g, "(") // "( word" -> "(word"
        // Pause + spoken command can stack marks (",." ) — keep the strongest one
        .replace(/[.,?!;]{2,}/g, (run) =>
            [...run].reduce((best, mark) => (MARK_STRENGTH[mark] > MARK_STRENGTH[best] ? mark : best))
        )
        .replace(/\s{2,}/g, " ")
        .trim();

    // Standalone "i" / "i'm" -> "I" / "I'm" (skips "i.e.")
    text = text.replace(/\bi\b(?!\.)/g, "I");

    // Capitalize the first letter of each new sentence
    text = text.replace(/([.?!]\s+)(\p{Ll})/gu, (_, gap, letter) => gap + letter.toUpperCase());
    if (capitalizeStart) {
        // Skip a leading "(" or quote, but not a "," — that continues the previous sentence
        text = text.replace(/^([\s"'(]*)(\p{Ll})/u, (_, lead, letter) => lead + letter.toUpperCase());
    }

    return text;
};

/** Adds a closing full stop if the text ends mid-sentence (Latin script only). */
export const endSentence = (text) => (/[A-Za-z0-9)]$/.test(text) ? `${text}.` : text);
